/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useNotifications } from '../../hooks/useNotifications';
import { motion } from 'framer-motion';
import { widgetPadding, animations, typography } from '../../utils/designTokens';
import moment from 'moment';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const NotificationsPage = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const navigate = useNavigate();
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    read: 'all',
    search: ''
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0
  });

  const { 
    items: notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    load,
    markRead,
    markAllRead,
    deleteNotification,
    deleteNotificationsBulk,
    reload: reloadNotifications
  } = useNotifications();

  // Selection state for bulk actions (align with admin)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Load notifications when pagination changes
  useEffect(() => {
    load({ 
      limit: pagination.limit, 
      offset: pagination.offset
    });
  }, [pagination.limit, pagination.offset]); // Remove 'load' from dependencies to prevent infinite re-renders

  // Apply filters locally for now (client scope)
  const filteredNotifications = notifications.filter(notification => {
    // Only show booking-related notifications for clients
    if (!notification.type.startsWith('booking.')) return false;
    
    if (filters.type !== 'all') {
      if (filters.type === 'cancelled' && !notification.type.includes('cancelled')) return false;
      if (filters.type === 'confirmed' && !notification.type.includes('confirmed') && !notification.type.includes('created')) return false;
      if (filters.type === 'rescheduled' && !notification.type.includes('updated')) return false;
    }
    
    if (filters.read !== 'all') {
      if (filters.read === 'read' && !notification.read) return false;
      if (filters.read === 'unread' && notification.read) return false;
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!notification.message.toLowerCase().includes(searchLower) && 
          !notification.type.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  // Row selection helpers (align with admin UX)
  const handleRowSelect = (id, checked) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id); else next.delete(id);
    setSelectedIds(next);
  };

  const handleSelectAll = (checked) => {
    if (checked && filteredNotifications?.length > 0) {
      setSelectedIds(new Set(filteredNotifications.map(n => n.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  // Handle pagination
  const handleNextPage = () => {
    if (hasMore) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  // Mark single notification as read
  const markAsRead = async (id) => {
    try {
      await markRead(id);
      // Reload notification count in header
      reloadNotifications();
    } catch (err) {
      // Error handled silently
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await markAllRead();
      reloadNotifications();
    } catch (err) {
      // Error handled silently
    }
  };

  // Bulk mark read (match admin)
  const markSelectedAsRead = async () => {
    if (selectedIds.size === 0) return;
    // Optimistic update
    selectedIds.forEach((id) => markRead(id));
    setSelectedIds(new Set());
    reloadNotifications();
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id);
    }
  };

  // Delete single/bulk (respect client scope)
  const handleDeleteNotification = async (id) => {
    try {
      await deleteNotification(id);
      reloadNotifications();
    } catch {}
  };

  const deleteSelectedNotifications = async () => {
    if (selectedIds.size === 0) return;
    try {
      await deleteNotificationsBulk(Array.from(selectedIds));
      setSelectedIds(new Set());
      reloadNotifications();
    } catch {}
  };

  // Get type pill color
  const getTypePillColor = (type) => {
    if (type.includes('cancelled')) return 'bg-red-500';
    if (type.includes('updated')) return 'bg-yellow-500'; // booking.updated = rescheduled
    if (type.includes('confirmed') || type.includes('created')) return 'bg-green-500'; // booking.created/confirmed = confirmed
    return 'bg-blue-500'; // Default for other booking types
  };

  // Format type for display
  const formatType = (type) => {
    if (type.includes('updated')) return 'Rescheduled';
    if (type.includes('created')) return 'Confirmed';
    if (type.includes('confirmed')) return 'Confirmed';
    if (type.includes('cancelled')) return 'Cancelled';
    return type.replace('.', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <div className="animate-pulse">
            <div className="h-8 rounded w-48 mb-2 bg-zinc-200 dark:bg-zinc-700"></div>
            <div className="h-4 rounded w-32 bg-zinc-200 dark:bg-zinc-700"></div>
          </div>
        </div>
        <Card>
          <div className="p-4 md:p-5">
            <div className="animate-pulse">
              <div className="h-6 rounded w-32 mb-4 bg-zinc-200 dark:bg-zinc-700"></div>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 rounded bg-zinc-200 dark:bg-zinc-700"></div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {t('booking.notifications_title')}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
              {unreadCount > 0 && (
                <span className="ml-2" style={{ color: ACCENT }}>
                  ({unreadCount} unread)
                </span>
              )}
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button
              onClick={() => navigate('/app/book')}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('booking.back_to_portal')}
            </button>
          </motion.div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader
          title="Filters"
          subtitle="Filter notifications by type, status, or search"
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
          }
        />
        <div className="p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="all">{t('booking.notifications_all')}</option>
                <option value="cancelled">{t('booking.status_cancelled_label')}</option>
                <option value="confirmed">Confirmed</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('booking.notifications_status')}
              </label>
              <select
                value={filters.read}
                onChange={(e) => handleFilterChange('read', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="all">All</option>
                <option value="unread">{t('booking.notifications_unread')}</option>
                <option value="read">{t('booking.notifications_read')}</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('booking.notifications_search')}
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex gap-3 flex-wrap">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={markAllAsRead}
              className="px-4 py-2 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg flex items-center gap-2"
              style={{ background: ACCENT }}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('booking.notifications_mark_all_read')}
            </motion.button>

            <motion.button
              whileHover={{ scale: selectedIds.size === 0 ? 1 : 1.05 }}
              whileTap={{ scale: selectedIds.size === 0 ? 1 : 0.95 }}
              onClick={markSelectedAsRead}
              disabled={selectedIds.size === 0}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${
                selectedIds.size === 0
                  ? 'bg-zinc-400 cursor-not-allowed text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-lg'
              }`}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
              </svg>
              Mark Selected as Read ({selectedIds.size})
            </motion.button>

            <motion.button
              whileHover={{ scale: selectedIds.size === 0 ? 1 : 1.05 }}
              whileTap={{ scale: selectedIds.size === 0 ? 1 : 0.95 }}
              onClick={deleteSelectedNotifications}
              disabled={selectedIds.size === 0}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${
                selectedIds.size === 0
                  ? 'bg-zinc-400 cursor-not-allowed text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white hover:shadow-lg'
              }`}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected ({selectedIds.size})
            </motion.button>
          </div>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Notifications List */}
      {!filteredNotifications || filteredNotifications.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
              <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              {t('booking.no_notifications')}
            </h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {filters.type !== 'all' || filters.read !== 'all' || filters.search
                ? 'Try adjusting your filters'
                : 'You\'re all caught up!'}
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="Notifications"
            subtitle={`${filteredNotifications.length} notifications`}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            }
            action={
              filteredNotifications && filteredNotifications.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filteredNotifications.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-[--accent] rounded focus:ring-[--accent]/20 border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50"
                    style={{ '--accent': ACCENT }}
                  />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">{t('booking.portal_select_all')}</span>
                </div>
              )
            }
          />
          <div className="overflow-x-auto">
            <div className="divide-y divide-white/10 dark:divide-white/10">
              {filteredNotifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  className="p-4 md:p-5 transition-colors cursor-pointer text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30"
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(notification.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRowSelect(notification.id, e.target.checked);
                      }}
                      className="mt-1 h-4 w-4 text-[--accent] rounded focus:ring-[--accent]/20 border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50"
                      style={{ '--accent': ACCENT }}
                    />

                    {/* Unread Dot */}
                    {!notification.read && (
                      <div className="mt-2 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: ACCENT }}></div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            notification.type.includes('cancelled') ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                            notification.type.includes('updated') ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                            notification.type.includes('confirmed') || notification.type.includes('created') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            {formatType(notification.type)}
                          </span>
                          <span className="text-sm text-zinc-500 dark:text-zinc-400">
                            {formatRelativeTime(notification.created_at)}
                          </span>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(notification.id);
                          }}
                          className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                          title="Delete notification"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </motion.button>
                      </div>

                      <p className="text-sm leading-relaxed text-zinc-900 dark:text-zinc-100">
                        {notification.message}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {filteredNotifications && filteredNotifications.length > 0 && (
        <Card className="mt-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-600 dark:text-zinc-400">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, filteredNotifications.length)} of {filteredNotifications.length} notifications
              </div>
              
              <div className="flex items-center gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0}
                  className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('booking.portal_previous')}
                </motion.button>
                <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(filteredNotifications.length / pagination.limit)}
                </span>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleNextPage}
                  disabled={!hasMore}
                  className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('booking.portal_next')}
                </motion.button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default NotificationsPage;
